import { createContext, useContext } from 'react';

// Request options that pin a hey-api call to a specific platform + live/sandbox mode by overriding
// the default client's global-session headers. Spread into an SDK call's options (or a mutation's
// variables). This is the same shape the root entity page uses for its own queries/mutations.
export interface EntityApiRequestOptions {
  headers: Record<string, string>;
  fetch: (request: Request) => Promise<Response>;
}

export const createEntityModeRequestOptions = (platformId: string, isLive: boolean): EntityApiRequestOptions => {
  const headers = {
    'Platform-Id': platformId,
    'Live-Mode': isLive ? 'Yes' : 'No',
  };

  return {
    headers,
    fetch: (request: Request) => {
      const requestHeaders = new Headers(request.headers);
      Object.entries(headers).forEach(([key, value]) => requestHeaders.set(key, value));
      return fetch(new Request(request, { headers: requestHeaders }));
    },
  };
};

// Pins the shared associated-person selector and create-person modal to an explicit platform + mode
// instead of the global session mode. This matters on the root entity page, whose Live/Sandbox tab
// is independent of the app's global mode: without it a person could be searched, looked up, or even
// created in the wrong mode — e.g. created in Live and then failing to link in Sandbox, leaving an
// orphaned entity. When unset (the default — the normal entities pages) the components use the
// global session mode via the default client.
export interface EntityApiMode {
  requestOptions: EntityApiRequestOptions;
  // Extra values that scope React Query caches to this platform + mode so they don't collide with
  // the global-session caches.
  scope: unknown[];
}

const EntityApiModeContext = createContext<EntityApiMode | undefined>(undefined);

export const EntityApiModeProvider = EntityApiModeContext.Provider;

export const useEntityApiMode = (): EntityApiMode | undefined => useContext(EntityApiModeContext);
